import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { LicenseCard } from "@/components/LicenseCard";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [plugins, setPlugins] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [pluginId, setPluginId] = useState("");
  const [coreId, setCoreId] = useState("");
  const [macAddress, setMacAddress] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [p, l] = await Promise.all([api.get("/plugins"), api.get("/licenses/me")]);
    setPlugins(p.data);
    setLicenses(l.data);
  };

  useEffect(() => {
    load();
  }, []);

  const generateTrial = async (e) => {
    e.preventDefault();
    if (!pluginId || !coreId.trim() || !macAddress.trim()) {
      toast.error("Select a plugin and enter your Locking ID and MAC address");
      return;
    }
    setLoading(true);
    try {
      await api.post("/licenses/trial", { plugin_id: pluginId, locking_id: coreId.trim(), mac_address: macAddress.trim() || null });
      toast.success("Trial license generated");
      setCoreId("");
      setMacAddress("");
      setPluginId("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const selectedPlugin = plugins.find((p) => p.id === pluginId);

  return (
    <AppShell
      title={`Hi, ${user.name?.split(" ")[0] || "there"}`}
      subtitle="Generate a signed trial license bound to your Locking ID, then drop the token into your plugin for offline verification."
    >
      <div className="grid lg:grid-cols-3 gap-6 mb-12">
        {/* Trial generator */}
        <Card className="lg:col-span-2 bg-zinc-950 border-zinc-800 rounded-md p-6">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-orange-500" />
            <h2 className="text-xl font-bold tracking-tight">Start a trial</h2>
          </div>
          <p className="text-sm text-zinc-400 mb-6">
            Instant self-service trial. Bound to a single Locking ID.
          </p>
          <form onSubmit={generateTrial} className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Plugin</Label>
              <Select value={pluginId} onValueChange={setPluginId}>
                <SelectTrigger
                  data-testid="trial-plugin-select"
                  className="rounded-sm bg-zinc-900 border-zinc-800 focus:ring-1 focus:ring-orange-500"
                >
                  <SelectValue placeholder="Select a plugin" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {plugins.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`plugin-option-${p.slug}`}>
                      {p.name} · v{p.version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Locking ID</Label>
              <Input
                data-testid="trial-coreid-input"
                value={coreId}
                onChange={(e) => setCoreId(e.target.value)}
                placeholder="e.g. QSC-CORE110F-A1B2C3"
                className="rounded-sm bg-zinc-900 border-zinc-800 font-mono focus-visible:ring-1 focus-visible:ring-orange-500"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                MAC Address
              </Label>
              <Input
                data-testid="trial-mac-input"
                value={macAddress}
                onChange={(e) => setMacAddress(e.target.value)}
                placeholder="e.g. 00:1A:2B:3C:4D:5E"
                className="rounded-sm bg-zinc-900 border-zinc-800 font-mono focus-visible:ring-1 focus-visible:ring-orange-500"
              />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between">
              <p className="text-xs text-zinc-500 font-mono">
                {selectedPlugin ? `${selectedPlugin.trial_days}-day trial` : "Select a plugin"}
              </p>
              <Button
                data-testid="generate-trial-btn"
                type="submit"
                disabled={loading}
                className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white font-semibold"
              >
                {loading ? "Signing..." : "Generate trial license"}
              </Button>
            </div>
          </form>
        </Card>

        {/* Verify link card */}
        <Card className="bg-zinc-950 border-zinc-800 rounded-md p-6 flex flex-col justify-between">
          <div>
            <KeyRound className="h-5 w-5 text-orange-500 mb-2" />
            <h3 className="font-bold text-lg tracking-tight">Offline verification</h3>
            <p className="text-sm text-zinc-400 mt-2">
              Every license is Ed25519 signed. Verify tokens online, or embed the public
              key in your Lua for offline checks.
            </p>
          </div>
          <a href="/verify" data-testid="verify-tool-link">
            <Button variant="outline" className="w-full mt-6 rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white">
              Verification tool & docs
            </Button>
          </a>
        </Card>
      </div>

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold tracking-tight">My licenses</h2>
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
          {licenses.length} total
        </span>
      </div>

      {licenses.length === 0 ? (
        <div
          data-testid="no-licenses"
          className="border border-dashed border-zinc-800 rounded-md p-12 text-center text-zinc-500 font-mono text-sm"
        >
          [ no licenses yet — generate a trial above ]
        </div>
      ) : (
        <div className="space-y-4">
          {licenses.map((lic) => (
            <LicenseCard key={lic.id} lic={lic} />
          ))}
        </div>
      )}
    </AppShell>
  );
}
